import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CVContext = createContext();

const defaultCVData = {
  personalInfo: {
    name: '',
    jobTitle: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    photo: null,
    permit: '',
  },
  summary: '',
  education: [],
  experience: [],
  skills: [],
  languages: [],
  interests: [],
};

const defaultPurchases = {
  cvDownload: false,
  coverLetter: false,
  pack: false,
  linkedin: false,
  jobAdapt: false,
  freeDownloadUsed: false,
};

const FREE_DOWNLOAD_KEY = 'cvly-free-download-used';
const CV_DATA_KEY = 'cvly-cv-data';
const CV_THEME_KEY = 'cvly-cv-theme';
const CV_TEMPLATE_KEY = 'cvly-cv-template';

export const CVProvider = ({ children }) => {
  const [cvData, setCvData] = useState(() => {
    try {
      const saved = localStorage.getItem(CV_DATA_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultCVData,
          ...parsed,
          personalInfo: { ...defaultCVData.personalInfo, ...parsed.personalInfo },
        };
      }
      return defaultCVData;
    } catch {
      return defaultCVData;
    }
  });

  const [cvTheme, setCvTheme] = useState(() => {
    return localStorage.getItem(CV_THEME_KEY) || 'blue';
  });

  const [cvTemplate, setCvTemplate] = useState(() => {
    return localStorage.getItem(CV_TEMPLATE_KEY) || 'professional';
  });

  const [purchases, setPurchases] = useState(defaultPurchases);
  const [subscription, setSubscription] = useState(null);
  const [purchasesLoaded, setPurchasesLoaded] = useState(false);

  const [generatedCoverLetter, setGeneratedCoverLetter] = useState(null);
  const [linkedinData, setLinkedinData] = useState(null);
  const [adaptedData, setAdaptedData] = useState(null);

  useEffect(() => {
    localStorage.setItem(CV_DATA_KEY, JSON.stringify(cvData));
  }, [cvData]);

  useEffect(() => {
    localStorage.setItem(CV_THEME_KEY, cvTheme);
    document.documentElement.setAttribute('data-cv-theme', cvTheme);
  }, [cvTheme]);

  useEffect(() => {
    localStorage.setItem(CV_TEMPLATE_KEY, cvTemplate);
  }, [cvTemplate]);

  const refreshPurchases = useCallback(async () => {
    try {
      const res = await fetch('/api/purchases/status', { credentials: 'include' });
      if (!res.ok) {
        setPurchasesLoaded(true);
        return;
      }
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setPurchasesLoaded(true);
        return;
      }
      const data = await res.json();
      const dbPurchases = data.purchases || {};
      setPurchases(prev => ({
        ...prev,
        ...dbPurchases,
      }));
      setSubscription(data.subscription || null);
      setPurchasesLoaded(true);
    } catch {
      setPurchasesLoaded(true);
    }
  }, []);

  useEffect(() => {
    refreshPurchases();
  }, [refreshPurchases]);

  const updatePersonalInfo = useCallback((field, value) => {
    setCvData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value,
      },
    }));
  }, []);

  const updateSummary = useCallback((value) => {
    setCvData(prev => ({ ...prev, summary: value }));
  }, []);

  const addEducation = useCallback((edu) => {
    setCvData(prev => ({
      ...prev,
      education: [...prev.education, { ...edu, id: Date.now() }],
    }));
  }, []);

  const removeEducation = useCallback((id) => {
    setCvData(prev => ({
      ...prev,
      education: prev.education.filter(e => e.id !== id),
    }));
  }, []);

  const addExperience = useCallback((exp) => {
    setCvData(prev => ({
      ...prev,
      experience: [...prev.experience, { ...exp, id: Date.now() }],
    }));
  }, []);

  const removeExperience = useCallback((id) => {
    setCvData(prev => ({
      ...prev,
      experience: prev.experience.filter(e => e.id !== id),
    }));
  }, []);

  const addSkill = useCallback((skill) => {
    if (skill && !cvData.skills.includes(skill)) {
      setCvData(prev => ({
        ...prev,
        skills: [...prev.skills, skill],
      }));
    }
  }, [cvData.skills]);

  const removeSkill = useCallback((skill) => {
    setCvData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill),
    }));
  }, []);

  const addLanguage = useCallback((lang) => {
    setCvData(prev => ({
      ...prev,
      languages: [...prev.languages, { ...lang, id: Date.now() }],
    }));
  }, []);

  const removeLanguage = useCallback((id) => {
    setCvData(prev => ({
      ...prev,
      languages: prev.languages.filter(l => l.id !== id),
    }));
  }, []);

  const addInterest = useCallback((interest) => {
    if (interest && !cvData.interests.includes(interest)) {
      setCvData(prev => ({
        ...prev,
        interests: [...prev.interests, interest],
      }));
    }
  }, [cvData.interests]);

  const removeInterest = useCallback((interest) => {
    setCvData(prev => ({
      ...prev,
      interests: prev.interests.filter(i => i !== interest),
    }));
  }, []);

  const unlockPurchase = useCallback((type) => {
    setPurchases(prev => ({ ...prev, [type]: true }));
  }, []);

  const markFreeDownloadUsed = useCallback(() => {
    setPurchases(prev => ({ ...prev, freeDownloadUsed: true }));
    localStorage.setItem(FREE_DOWNLOAD_KEY, 'true');
  }, []);

  const canDownloadFree = useCallback(() => {
    const localStorageCheck = localStorage.getItem(FREE_DOWNLOAD_KEY) === 'true';
    return !purchases.freeDownloadUsed && !localStorageCheck;
  }, [purchases.freeDownloadUsed]);

  const hasPaidCVAccess = useCallback(() => {
    return purchases.cvDownload || purchases.pack || !!subscription;
  }, [purchases.cvDownload, purchases.pack, subscription]);

  const hasActiveSubscription = useCallback(() => {
    return !!subscription;
  }, [subscription]);

  const reorderExperience = useCallback((fromIndex, toIndex) => {
    setCvData(prev => {
      const items = [...prev.experience];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      return { ...prev, experience: items };
    });
  }, []);

  const reorderEducation = useCallback((fromIndex, toIndex) => {
    setCvData(prev => {
      const items = [...prev.education];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      return { ...prev, education: items };
    });
  }, []);

  const resetCV = useCallback(() => {
    setCvData(defaultCVData);
    setGeneratedCoverLetter(null);
    setLinkedinData(null);
    setAdaptedData(null);
  }, []);

  return (
    <CVContext.Provider
      value={{
        cvData,
        cvTheme,
        setCvTheme,
        cvTemplate,
        setCvTemplate,
        purchases,
        subscription,
        generatedCoverLetter,
        setGeneratedCoverLetter,
        linkedinData,
        setLinkedinData,
        adaptedData,
        setAdaptedData,
        updatePersonalInfo,
        updateSummary,
        addEducation,
        removeEducation,
        addExperience,
        removeExperience,
        addSkill,
        removeSkill,
        addLanguage,
        removeLanguage,
        addInterest,
        removeInterest,
        reorderExperience,
        reorderEducation,
        unlockPurchase,
        markFreeDownloadUsed,
        canDownloadFree,
        hasPaidCVAccess,
        hasActiveSubscription,
        refreshPurchases,
        resetCV,
      }}
    >
      {children}
    </CVContext.Provider>
  );
};

export const useCV = () => {
  const context = useContext(CVContext);
  if (!context) {
    throw new Error('useCV must be used within a CVProvider');
  }
  return context;
};
